import { ConfirmDialog, ConfirmDialogProps } from 'bento';
import { createPortal } from 'react-dom';
import React, { ReactElement, ReactNode, createContext, useContext, useState } from 'react';

export interface IAsyncRenderContext {
  /**
   * Immediately renders the component returned from the render function and
   * waits for the user's response.
   *
   * @example
   *  try {
   *    await show((res, rej) => <MyComponent onCancel={rej} onComplete={res}/>);
   *  } catch {
   *    return;
   *  }
   */
  show(renderFn: RenderFn): Promise<unknown>;
  /**
   * Immediately renders a ConfirmAction and waits for the user's response
   *
   * @example
   *  try {
   *    await confirm('Do you want to overbook?');
   *  } catch {
   *    return;
   *  }
   */
  confirm(text: string | ReactElement, options?: ConfigurableOptions): Promise<unknown>;
}

type RenderFn = (resolve: (value: unknown) => void, reject: () => void) => ReactElement;

type ConfigurableOptions = Pick<
  ConfirmDialogProps,
  'cancelText' | 'continueText' | 'isDestructive' | 'title'
>;

const AsyncRenderContext = createContext<IAsyncRenderContext | undefined>(undefined);

/**
 * Provides the ability to render a component in our app programmatically.
 * The component will be displayed until the promise is settled.
 * This is a blocking action that the user must resolve before continuing.
 *
 * This replaces interactivePromise + confirm.show()
 */
export const AsyncRenderProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [renderedElement, setRenderedElement] = useState<ReactNode>();

  const createPromise = (renderFn: RenderFn) =>
    new Promise((resolve, reject) => {
      setRenderedElement(renderFn(resolve, reject));
    }).finally(() => setRenderedElement(null));

  const renderConfirmation =
    (text: string | ReactElement, options: ConfigurableOptions = {}): RenderFn =>
    (resolve, reject) => (
      <ConfirmDialog {...options} onCancel={reject} onContinue={resolve} open>
        {text}
      </ConfirmDialog>
    );

  const context = {
    confirm: (
      text: string | ReactElement,
      options: ConfigurableOptions = {},
      useConfirmDialog = false
    ) => createPromise(renderConfirmation(text, options)),

    show: (renderFn: RenderFn) => createPromise(renderFn),
  };

  return (
    <AsyncRenderContext.Provider value={context}>
      {children}
      {renderedElement && createPortal(renderedElement, document.body)}
    </AsyncRenderContext.Provider>
  );
};

export const useAsyncRender = () => {
  const context = useContext(AsyncRenderContext);
  if (context === undefined) {
    throw new Error('useAsyncRender must be used within a AsyncRenderProvider');
  }
  return context;
};

export interface WithAsyncRenderProps {
  asyncRender: IAsyncRenderContext;
}

/**
 * HOC wrapper to utilize AsyncRender context in a class component
 */
export const withAsyncRender = <P,>(
  Component: React.ComponentType<React.PropsWithChildren<P & WithAsyncRenderProps>>
) => {
  const WithAsyncRender = (props: P) => {
    const asyncRender = useAsyncRender();
    // The any comes from a bad interaction between emotion and react types.
    // See: https://github.com/emotion-js/emotion/issues/2169
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    return <Component asyncRender={asyncRender} {...(props as any)} />;
  };

  const componentName = Component.displayName || Component.name || 'Component';
  WithAsyncRender.displayName = `WithAsyncRender(${componentName})`;

  return WithAsyncRender;
};
